/**
 * Public IND catalog — the source of truth for what shows on the public
 * commons.chronabio.ai landing page.
 *
 * The data file lives at `commons/public/catalog.json` so it ALSO ships
 * to dist/catalog.json as a public URL surface. We import it directly
 * here so the entries are bundled into the JS payload and rendered on
 * React's first commit (no fetch, no loading state, no flash).
 *
 * For SEO, the same JSON is read by the `inject-catalog` Vite plugin
 * (vite.config.ts) at build time and pre-rendered into `index.html`
 * inside `#root`. That way `view-source:` shows the catalog entries
 * without relying on JS execution — crawlers and curl both see real
 * content. React then replaces `#root` on mount with the same data;
 * because the structure matches, there's no visible flash.
 *
 * Per A2F-735 (option C): hand-maintained JSON for now. Migrate to a
 * backend endpoint when contributor pipeline justifies it.
 */

import catalogData from '../../public/catalog.json'

export interface CatalogPublication {
  title: string
  venue: string
  doi: string | null
  url: string
}

/** A verbatim FDA quote + its attribution line, for the per-IND page. */
export interface CatalogFdaQuote {
  /** Verbatim text of the quote (no surrounding quotation marks). */
  text: string
  /** Attribution, e.g. "FDA, End-of-Phase-2 meeting minutes, 19 Jul 2017". */
  attribution: string
}

export interface CatalogIND {
  slug: string
  name: string
  /**
   * FDA-assigned IND number (e.g. "143480"). Canonical join key to the
   * backend `Portfolio.indNumber` field — env-independent, public after
   * clearance, stable across project renames. Required for every entry.
   */
  ind_number: string
  long_name: string
  sponsor: string
  sponsor_affiliation: string | null
  indication: string
  modality: string
  /** e.g. "Research IND", "Commercial IND". null until known. */
  ind_type: string | null
  /** Display status: "Cleared", "IND cleared", "Pre-IND", etc. */
  ind_status: string
  /** ISO yyyy-mm-dd, or null if unknown / not yet cleared. */
  ind_clearance_date: string | null
  /** Number of eCTD submission sequences in this IND portfolio. */
  submission_count: number | null
  /** Protein therapeutics — count of unique sequences in the dossier. */
  sequence_count: number | null
  /** Total document count in the eCTD package, when known. */
  document_count: number | null
  /** True if the dossier bundles Pre-IND meeting correspondence too. */
  includes_pre_ind: boolean
  /** Reuse license tag, e.g. "CC-BY-4.0". null = not declared. */
  license: string | null
  /** Companion publication for the program, when one exists. */
  publication: CatalogPublication | null
  /** Free-text source attribution. */
  source: string
  /** Outbound URL for the source repository / project page. */
  source_url: string | null
  description_short: string
  /**
   * Long-form "About this submission" narrative for the /ind/{slug} page.
   * When present it renders below (not instead of) description_short.
   * Optional — entries without it fall back to description_short alone.
   */
  about_long?: string
  /** Pull-quote from the FDA correspondence, shown on the /ind page. */
  fda_quote?: CatalogFdaQuote | null
  /**
   * "What the FDA dialogue reveals" — short insight bullets. Each string
   * may contain a leading bold lead-in delimited by ** ** (rendered as
   * <strong>); the rest is plain text. Keep markup minimal.
   */
  dialogue_reveals?: string[]
  /** "Best studied for" — one sentence of use-cases for the dossier. */
  best_studied_for?: string
}

export interface Catalog {
  inds: CatalogIND[]
}

export const catalog: Catalog = catalogData as Catalog
